/**
 * CDB-TQS API — Graph, Entities, Facts, Knowledge Blocks and Episode Images
 */

import { logApiCall, logApiResponse, logError } from "@/lib/logger";
import type { GraphAllDataResponse, GraphStatsResponse } from "@/types/graph";
import type {
  EntityListResponse,
  EntityNode,
  EntityDetailsResponse,
  UpdateEntityStatusRequest,
  EntityListParams,
} from "@/types/entities";
import type { FactListResponse } from "@/components/knowledge-extractor/facts/types";
import type { KnowledgeBlockDetails, KnowledgeBlockListResponse, KnowledgeBlockStats } from "@/types/cdb-tqs-knowledge-blocks";
import { CDB_TQS_API_BASE_URL, fetchWithLogging, getAuthHeaders } from "./http";

export const graphApi = {
  /**
   * Get graph statistics (entity and relationship counts)
   * @returns Graph statistics
   */
  async getGraphStats(): Promise<GraphStatsResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/graph/stats`,
      {
        method: "GET",
      }
    );

    return response.data as GraphStatsResponse;
  },

  /**
   * Get all graph data (entities and edges)
   * @param params - Query parameters (limit, query, knowledge_areas)
   * @returns All entities and edges
   */
  async getGraphAll(params?: {
    limit?: number;
    query?: string;
    knowledge_areas?: string[];
  }): Promise<GraphAllDataResponse> {
    const queryParams: Record<string, string> = {};

    if (params?.limit) {
      queryParams.limit = String(params.limit);
    }
    if (params?.query) {
      queryParams.query = params.query;
    }
    if (params?.knowledge_areas && params.knowledge_areas.length > 0) {
      queryParams.knowledge_areas = params.knowledge_areas.join(",");
    }

    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/graph/all`,
      {
        method: "GET",
        params: queryParams,
      }
    );

    return response.data as GraphAllDataResponse;
  },

  /**
   * Delete a graph entity
   * @param uuid - Entity UUID
   */
  async deleteGraphEntity(uuid: string): Promise<void> {
    // Excel uuids may contain ``?``/``/``/``#`` — see getEntityDetails comment.
    await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/graph/entities/${encodeURIComponent(uuid)}`, {
      method: "DELETE",
    });
  },

  /**
   * Update edge verification status
   * @param uuid - Edge UUID
   * @param data - Verification data
   * @returns Updated edge
   */
  async updateEdgeVerification(
    uuid: string,
    data: {
      verification_status: "pending" | "verified" | "rejected";
      verified_by: string;
      verification_comments?: string;
    }
  ): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/graph/edges/${uuid}/verify`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );

    return response.data;
  },

  /**
   * Get list of entities with filtering and pagination
   * @param params - Query parameters for filtering entities
   * @returns List of entities with counts by status
   */
  async listEntities(
    params: EntityListParams = {}
  ): Promise<EntityListResponse> {
    const queryParams: Record<string, string | string[]> = {};

    if (params.knowledge_area) {
      queryParams.knowledge_area = params.knowledge_area;
    }
    if (params.status) {
      queryParams.status = params.status;
    }
    if (params.entity_type) {
      queryParams.entity_type = params.entity_type;
    }
    if (params.search) {
      queryParams.search = params.search;
    }
    if (params.document_id) {
      queryParams.document_id = params.document_id;
    }
    if (params.limit !== undefined) {
      queryParams.limit = String(params.limit);
    }
    if (params.offset !== undefined) {
      queryParams.offset = String(params.offset);
    }

    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/entities`,
      {
        method: "GET",
        params: queryParams,
      }
    );

    return response.data as EntityListResponse;
  },

  /**
   * Get detailed information about an entity including relationships, Q&A, and documents
   * @param uuid - Entity UUID
   * @returns Entity details with all related information
   */
  async getEntityDetails(
    uuid: string,
    knowledgeArea: string,
    documentId?: string | null
  ): Promise<EntityDetailsResponse> {
    // Excel-imported entities have synthetic uuids built from
    // ``"<type>-<pk1>|<pk2>"`` (excel_processor_service.py:428) — those
    // can contain ``?`` / ``/`` / ``#`` from the original cell values.
    // Without encoding, a literal ``?`` in the path is interpreted as
    // the query-string boundary by the server and the rest of the
    // entity id is dropped → 404. ``encodeURIComponent`` percent-
    // escapes those characters; for real UUIDs it's a no-op.
    const params: Record<string, string> = { knowledge_area: knowledgeArea };
    // When set, the backend returns only relationships extracted from this
    // document's episodes — the same node is mentioned across many docs, so
    // the Verifier scopes its fact list to the document under review.
    if (documentId) params.document_id = documentId;

    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/entities/${encodeURIComponent(uuid)}/details`,
      {
        method: "GET",
        params,
      }
    );

    return response.data as EntityDetailsResponse;
  },

  /**
   * Update entity status and optionally edit name/summary
   * @param uuid - Entity UUID
   * @param updates - Status and optional field updates
   * @returns Updated entity
   */
  async updateEntityStatus(
    uuid: string,
    updates: UpdateEntityStatusRequest
  ): Promise<EntityNode> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/entities/${encodeURIComponent(uuid)}/status`,
      {
        method: "PATCH",
        body: JSON.stringify(updates),
      }
    );

    return response.data as EntityNode;
  },

  /**
   * Get all facts with optional filters
   * @param params - Optional filters for knowledge area and status
   * @returns List of facts with counts
   */
  async listFacts(
    params: {
      knowledgeArea?: string;
      status?: string;
      documentId?: string;
      limit?: number;
      offset?: number;
      language?: string;
    } = {}
  ): Promise<FactListResponse> {
    const queryParams: Record<string, string> = {};

    if (params.knowledgeArea) {
      queryParams.knowledge_area = params.knowledgeArea;
    }
    if (params.status && params.status !== "all") {
      queryParams.status = params.status;
    }
    if (params.documentId) {
      queryParams.document_id = params.documentId;
    }
    if (params.limit) {
      queryParams.limit = String(params.limit);
    }
    if (params.offset) {
      queryParams.offset = String(params.offset);
    }
    if (params.language) {
      queryParams.language = params.language;
    }

    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/facts`, {
      method: "GET",
      params: queryParams,
    });

    return response.data as FactListResponse;
  },

  /**
   * Get specific facts by their UUIDs
   * More efficient than listing all facts when you know the exact UUIDs
   * @param uuids - Array of fact UUIDs to retrieve
   * @param knowledgeArea - Knowledge area (required for routing)
   * @returns List of matching facts
   */
  async getFactsByUuids(
    uuids: string[],
    knowledgeArea: string
  ): Promise<FactListResponse> {
    if (uuids.length === 0) {
      return {
        facts: [],
        total: 0,
        open_count: 0,
        verified_count: 0,
        updated_count: 0,
        rejected_count: 0,
      };
    }

    const queryParams: Record<string, string> = {
      uuids: uuids.join(','),
      knowledge_area: knowledgeArea,
    };

    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/facts/by-uuids`, {
      method: "GET",
      params: queryParams,
    });

    return response.data as FactListResponse;
  },

  /**
   * List knowledge blocks (per-document fact/entity verification stats).
   * Used by the Document table to show verification progress per document.
   */
  async listKnowledgeBlocks(params?: {
    knowledgeArea?: string
    onlyIncomplete?: boolean
  }): Promise<KnowledgeBlockListResponse> {
    const queryParams: Record<string, string> = {}
    if (params?.knowledgeArea) queryParams.knowledge_area = params.knowledgeArea
    if (params?.onlyIncomplete) queryParams.only_incomplete = 'true'

    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-blocks`,
      { method: 'GET', params: queryParams }
    )
    return response.data as KnowledgeBlockListResponse
  },

  /**
   * Per-document fact/entity stats — lightweight (no Q&A/fact/entity
   * payload, just the counts). Used by the Verifier's progress bar
   * when a single document is selected. See cdb_tqs#349.
   */
  /**
   * Everything extracted from one document — statements and concepts, scoped
   * to the document's own episodes (#630).
   */
  async getKnowledgeBlockDetails(params: {
    documentId: string;
    knowledgeArea: string;
  }): Promise<KnowledgeBlockDetails> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-blocks/${encodeURIComponent(params.documentId)}`,
      { method: "GET", params: { knowledge_area: params.knowledgeArea } }
    );
    return response.data as KnowledgeBlockDetails;
  },

  /**
   * Make one person responsible for reviewing a document, or nobody (#631).
   *
   * Goes through the BFF like everything else here, but not through its
   * catch-all: the BFF has an explicit route for this path that checks the
   * caller is an admin or a supervisor. TQS itself only knows knowledge areas.
   */
  async setDocumentAssignment(params: {
    documentId: string
    knowledgeArea: string
    assignedTo: string | null
  }): Promise<void> {
    await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-blocks/${encodeURIComponent(params.documentId)}/assignment`,
      {
        method: 'PATCH',
        params: { knowledge_area: params.knowledgeArea },
        body: JSON.stringify({ assigned_to: params.assignedTo }),
      }
    )
  },

  async getKnowledgeBlockStats(params: {
    documentId: string
    knowledgeArea: string
  }): Promise<KnowledgeBlockStats> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-blocks/${encodeURIComponent(params.documentId)}/stats`,
      {
        method: 'GET',
        params: { knowledge_area: params.knowledgeArea },
      }
    )
    return response.data as KnowledgeBlockStats
  },

  /**
   * KA-aggregate or cross-KA aggregate stats — deduplicated facts +
   * entities across the requested scope. Used by the Verifier's
   * progress bar when no single document is selected. See cdb_tqs#349.
   */
  async getAggregateKnowledgeBlockStats(params?: {
    knowledgeArea?: string
  }): Promise<KnowledgeBlockStats> {
    const queryParams: Record<string, string> = {}
    if (params?.knowledgeArea) queryParams.knowledge_area = params.knowledgeArea

    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-blocks/aggregate-stats`,
      { method: 'GET', params: queryParams }
    )
    return response.data as KnowledgeBlockStats
  },

  /**
   * Update a fact's status (and optionally other fields)
   */
  async updateFactStatus(
    factUuid: string,
    knowledgeArea: string,
    updates: Record<string, unknown>
  ): Promise<any> {
    // Excel facts use ``elementId(r)`` as their synthetic uuid — those
    // are short alphanumerics, but Graphiti facts also pass through
    // here and could in theory carry punctuation. Encode unconditionally
    // for safety; for clean uuids it's a no-op.
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/facts/${encodeURIComponent(factUuid)}/status`,
      {
        method: "PATCH",
        params: { knowledge_area: knowledgeArea },
        body: JSON.stringify(updates),
      }
    );
    return response.data;
  },

  /**
   * Get specific entities by their UUIDs
   * More efficient than listing all entities when you know the exact UUIDs
   * @param uuids - Array of entity UUIDs to retrieve
   * @param knowledgeArea - Knowledge area (required for routing)
   * @returns List of matching entities
   */
  async getEntitiesByUuids(
    uuids: string[],
    knowledgeArea: string
  ): Promise<EntityListResponse> {
    if (uuids.length === 0) {
      return {
        entities: [],
        total: 0,
        open_count: 0,
        verified_count: 0,
        updated_count: 0,
        rejected_count: 0,
      };
    }

    const queryParams: Record<string, string> = {
      uuids: uuids.join(','),
      knowledge_area: knowledgeArea,
    };

    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/entities/by-uuids`, {
      method: "GET",
      params: queryParams,
    });

    return response.data as EntityListResponse;
  },

  /**
   * Upload image to episode (stores in client bucket)
   * @param episodeId - Episode UUID
   * @param file - Image file
   * @returns Created image metadata
   */
  async uploadEpisodeImage(episodeId: string, file: File): Promise<any> {
    const formData = new FormData()
    formData.append('file', file)

    const url = `${CDB_TQS_API_BASE_URL}/sessions/episodes/${episodeId}/images/upload`

    logApiCall('POST', url, { filename: file.name, episodeId })

    try {
      const authHeaders = await getAuthHeaders()

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: formData,
      })

      const data = response.headers.get('content-type')?.includes('application/json')
        ? await response.json()
        : await response.text()

      logApiResponse('POST', url, response.status, data)

      if (!response.ok) {
        throw new Error(`Image upload failed: ${response.status} ${response.statusText}`)
      }

      return data
    } catch (error) {
      logError(error as Error, {
        method: 'POST',
        url,
        context: 'Episode image upload failed',
      })
      throw error
    }
  },

  /**
   * Get episode image URL (authenticated via JWT)
   * @param imageId - Image UUID
   * @returns Image URL
   */
  getEpisodeImageUrl(imageId: string): string {
    return `${CDB_TQS_API_BASE_URL}/sessions/episodes/images/${imageId}/file`
  },

  /**
   * Delete episode image
   * @param imageId - Image UUID
   */
  async deleteEpisodeImage(imageId: string): Promise<void> {
    await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/sessions/episodes/images/${imageId}`, {
      method: 'DELETE',
    })
  },
};
